import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, ArrowLeft } from 'lucide-react';
import StepNavigator from '../components/StepNavigator';
import TriggerConfiguration from '../components/TriggerConfiguration';
import EmailStepEditor from '../components/step-editors/EmailStepEditor';
import WaitStepEditor from '../components/step-editors/WaitStepEditor';
import TaskStepEditor from '../components/step-editors/TaskStepEditor';
import SlackStepEditor from '../components/step-editors/SlackStepEditor';
import AddStepMenu from '../components/AddStepMenu';
// Example sequence data for Analytics Upsell Sequence
const analyticsUpsellSequence = {
  id: '1',
  name: 'Analytics Upsell Sequence',
  status: 'Active',
  triggerAlert: 'Potential Analytics Upsell (High Manual Reporting)',
  personaFilter: 'Enterprise Customers',
  schedule: 'Check daily at 9 AM, Re-enroll after 90 days',
  steps: [{
    id: 'step1',
    type: 'email',
    title: 'Automatic Email',
    summary: 'Analytics Interest Feeler',
    data: {
      subject: 'Getting more insights from your {{Company.Name}} data',
      body: `Hi {{Contact.FirstName}},
I'm hoping to connect with a few revenue leaders frustrated with relying on manual report-digging and gut-feel to find the next big upsell, knowing valuable opportunities are buried in call transcripts and support tickets.
Our team is building an AI Revenue Analyst that connects to Gong, HubSpot/Salesforce, and your data warehouse. It proactively analyzes all your GTM data to find the hidden revenue signals your team is too busy to look for.
If your team received a daily brief of 3-5 new, high-quality opportunities they hadn't seen before, would you potentially want more info, {{Contact.FirstName}}?`
    }
  }, {
    id: 'step2',
    type: 'wait',
    title: 'Wait for a period of time',
    summary: '3 Business Days',
    data: {
      duration: 3,
      unit: 'days',
      businessDaysOnly: true
    }
  }, {
    id: 'step3',
    type: 'task',
    title: 'Create HubSpot Task',
    summary: 'Follow-up call about analytics interest',
    data: {
      assignTo: '{{Account.Owner}}',
      taskDetails: 'Call {{Contact.FirstName}} to follow up on analytics email. They exported {{Alert.TriggerValue}} reports last month.',
      priority: 'High'
    }
  }, {
    id: 'step4',
    type: 'slack',
    title: 'Notify Slack Channel',
    summary: 'Post to #upsell-opportunities',
    data: {
      channel: '#upsell-opportunities',
      message: '📈 New Analytics Opp: {{Account.Name}} exported {{Alert.TriggerValue}} reports. Follow-up task created for {{Account.Owner}}.'
    }
  }]
};
// Sample data for other sequences
const sequenceData = {
  '1': analyticsUpsellSequence,
  '2': {
    id: '2',
    name: 'Onboarding Risk Intervention',
    status: 'Active',
    triggerAlert: 'Manufacturer Not Using Assemblies',
    personaFilter: '',
    schedule: 'Check daily at 9 AM, Re-enroll after 90 days',
    steps: [{
      id: 'step1',
      type: 'task',
      title: 'Create HubSpot Task',
      summary: 'Proactive outreach task for CSM',
      data: {
        assignTo: '{{Account.CSM}}',
        taskDetails: 'Proactive Outreach: {{Account.Name}} is a manufacturer but has not used Assemblies. Schedule a health check call to offer training.',
        priority: 'High'
      }
    }, {
      id: 'step2',
      type: 'slack',
      title: 'Notify Slack Channel',
      summary: 'Post to #cs-red-flags',
      data: {
        channel: '#cs-red-flags',
        message: '⚠️ Onboarding Risk: {{Account.Name}} is not using Assemblies. Task created for CSM {{Account.Owner}}.'
      }
    }]
  },
  '3': {
    id: '3',
    name: 'License Overage Billing',
    status: 'Active',
    triggerAlert: 'User Overage - License Limit Exceeded',
    personaFilter: '',
    schedule: 'Check daily at 9 AM, Never re-enroll',
    steps: [{
      id: 'step1',
      type: 'task',
      title: 'Create HubSpot Task',
      summary: 'Approval task for Account Manager',
      data: {
        assignTo: '{{Account.Owner}}',
        taskDetails: 'Approval Required: {{Account.Name}} has added {{Alert.TriggerValue}} users above their subscription limit. Please verify and approve billing update.',
        priority: 'High'
      }
    }, {
      id: 'step2',
      type: 'slack',
      title: 'Notify Slack Channel',
      summary: 'Post to #billing-alerts',
      data: {
        channel: '#billing-alerts',
        message: '💰 License Expansion: {{Account.Name}} is {{Alert.TriggerValue}} users over their limit. Awaiting AM approval in HubSpot.'
      }
    }]
  }
};
// Default new sequence template
const newSequenceTemplate = {
  id: 'new',
  name: 'New Sequence',
  status: 'Draft',
  triggerAlert: 'Select a Trigger Alert',
  personaFilter: '',
  schedule: 'Check daily at 9 AM, Re-enroll after 90 days',
  steps: []
};
const SequenceEditor: React.FC = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const [sequence, setSequence] = useState<any>(null);
  const [selectedStepId, setSelectedStepId] = useState<string>('');
  const [isAddStepMenuOpen, setIsAddStepMenuOpen] = useState(false);
  useEffect(() => {
    // Load sequence data based on ID
    if (id === 'new') {
      setSequence(newSequenceTemplate);
      setSelectedStepId('');
    } else if (id && sequenceData[id as keyof typeof sequenceData]) {
      setSequence(sequenceData[id as keyof typeof sequenceData]);
      if (sequenceData[id as keyof typeof sequenceData].steps.length > 0) {
        setSelectedStepId(sequenceData[id as keyof typeof sequenceData].steps[0].id);
      } else {
        setSelectedStepId('');
      }
    } else {
      // Fallback to first sequence if ID not found
      setSequence(analyticsUpsellSequence);
      setSelectedStepId(analyticsUpsellSequence.steps[0].id);
    }
  }, [id]);
  if (!sequence) {
    return <div className="flex items-center justify-center h-full" data-id="element-1477">Loading...</div>;
  }
  const selectedStep = sequence.steps.find((step: any) => step.id === selectedStepId);
  const handleStepSelect = (stepId: string) => {
    setSelectedStepId(stepId);
  };
  const handleAddStep = () => {
    setIsAddStepMenuOpen(true);
  };
  const handleSelectStepType = (type: string) => {
    // Create a new step based on the selected type
    const newStep = {
      id: `step${sequence.steps.length + 1}`,
      type,
      title: getStepTitle(type),
      summary: 'New step - configure details',
      data: getDefaultDataForStepType(type)
    };
    // Add the step to the sequence
    setSequence({
      ...sequence,
      steps: [...sequence.steps, newStep]
    });
    // Select the new step
    setSelectedStepId(newStep.id);
  };
  const getStepTitle = (type: string) => {
    switch (type) {
      case 'email':
        return 'Automatic Email';
      case 'wait':
        return 'Wait for a period of time';
      case 'task':
        return 'Create HubSpot Task';
      case 'slack':
        return 'Notify Slack Channel';
      default:
        return 'New Step';
    }
  };
  const getDefaultDataForStepType = (type: string) => {
    switch (type) {
      case 'email':
        return {
          subject: '',
          body: ''
        };
      case 'wait':
        return {
          duration: 1,
          unit: 'days',
          businessDaysOnly: true
        };
      case 'task':
        return {
          assignTo: '{{Account.Owner}}',
          taskDetails: '',
          priority: 'Medium'
        };
      case 'slack':
        return {
          channel: '#general',
          message: ''
        };
      default:
        return {};
    }
  };
  const handleTriggerChange = (trigger: string) => {
    setSequence({
      ...sequence,
      triggerAlert: trigger
    });
  };
  const handlePersonaChange = (persona: string) => {
    setSequence({
      ...sequence,
      personaFilter: persona
    });
  };
  const handleSaveChanges = () => {
    // In a real app, you'd save the sequence data
    alert('Changes saved!');
  };
  const handleDeactivate = () => {
    // In a real app, you'd deactivate the sequence
    setSequence({
      ...sequence,
      status: sequence.status === 'Active' ? 'Inactive' : 'Active'
    });
  };
  const handleBackToList = () => {
    navigate('/sequences');
  };
  const renderStepEditor = () => {
    if (!selectedStep) {
      return <div className="flex flex-col items-center justify-center h-64 text-gray-500" data-id="element-1478">
          <p className="mb-4" data-id="element-1479">
            Select a step from the left panel or add a new step
          </p>
          <button onClick={handleAddStep} className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1480">
            Add Your First Step
          </button>
        </div>;
    }
    switch (selectedStep.type) {
      case 'email':
        return <EmailStepEditor subject={selectedStep.data.subject} body={selectedStep.data.body} onUpdate={data => {
          // Update the step data
          console.log('Email step updated:', data);
        }} data-id="element-1481" />;
      case 'wait':
        return <WaitStepEditor duration={selectedStep.data.duration} unit={selectedStep.data.unit} businessDaysOnly={selectedStep.data.businessDaysOnly} onUpdate={data => {
          // Update the step data
          console.log('Wait step updated:', data);
        }} data-id="element-1482" />;
      case 'task':
        return <TaskStepEditor assignTo={selectedStep.data.assignTo} taskDetails={selectedStep.data.taskDetails} priority={selectedStep.data.priority} onUpdate={data => {
          // Update the step data
          console.log('Task step updated:', data);
        }} data-id="element-1483" />;
      case 'slack':
        return <SlackStepEditor channel={selectedStep.data.channel} message={selectedStep.data.message} onUpdate={data => {
          // Update the step data
          console.log('Slack step updated:', data);
        }} data-id="element-1484" />;
      default:
        return <div data-id="element-1485">Unknown step type</div>;
    }
  };
  return <div className="h-full flex flex-col" data-id="element-1486">
      {/* Header */}
      <div className="flex justify-between items-center mb-4" data-id="element-1487">
        <div className="flex items-center gap-2" data-id="element-1488">
          <button onClick={handleBackToList} className="p-1 hover:bg-gray-100 rounded-full" data-id="element-1489">
            <ArrowLeft size={20} data-id="element-1490" />
          </button>
          <h1 className="text-2xl font-semibold" data-id="element-1491">{sequence.name}</h1>
          <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${sequence.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`} data-id="element-1492">
            {sequence.status}
          </span>
        </div>
        <div className="flex gap-3" data-id="element-1493">
          <button onClick={handleDeactivate} className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50" data-id="element-1494">
            {sequence.status === 'Active' ? 'Deactivate' : 'Activate'}
          </button>
          <button onClick={handleSaveChanges} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" data-id="element-1495">
            Save Changes
          </button>
        </div>
      </div>

      {/* Main content */}
      <div className="flex flex-1 bg-white rounded-lg shadow overflow-hidden" data-id="element-1496">
        {/* Left panel: Step Navigator */}
        <div className="w-[280px] flex-shrink-0 border-r" data-id="element-1497">
          <StepNavigator steps={sequence.steps} selectedStepId={selectedStepId} onSelectStep={handleStepSelect} onAddStep={handleAddStep} data-id="element-1498" />
        </div>

        {/* Right panel: Configuration Area */}
        <div className="flex-1 p-6 overflow-y-auto" data-id="element-1499">
          {/* Trigger Configuration */}
          <TriggerConfiguration triggerAlert={sequence.triggerAlert} personaFilter={sequence.personaFilter} onTriggerChange={handleTriggerChange} onPersonaChange={handlePersonaChange} data-id="element-1500" />
          {/* Step Editor */}
          <div data-id="element-1501">{renderStepEditor()}</div>
        </div>
      </div>

      {/* Add Step Menu Modal */}
      <AddStepMenu isOpen={isAddStepMenuOpen} onClose={() => setIsAddStepMenuOpen(false)} onSelectStepType={handleSelectStepType} data-id="element-1502" />
    </div>;
};
export default SequenceEditor;